/**
 * @file main.component.ts
 */
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Observable, Subscription, defer, of, Subject } from 'rxjs';
import { map, shareReplay, filter, take, switchMap, catchError, tap } from 'rxjs/operators';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Router, NavigationStart, ActivatedRoute, NavigationEnd } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import {
  EligibilityVerificationResponse,
  IntegrationType,
  MailOrderFamilyMember,
  Member,
  MemberProfile,
} from '@models/interfaces';
import { AuthService, LogService, MultilingualService } from '@core/services';
import { AppConstants, ELIGIBILITY_STATUS } from '@root/app/app.constants';
import { PAGE_NAME } from '@models/enums';
import {
  AppCenterAnalytics,
  AppInsightsService,
  CartService,
  ClientsService,
  CmsService,
  MailOrderProfileEnrollmentService,
  MemberService,
  UserService,
} from '@shared/services';
import {
  CommonConfirmationPopupComponent,
  DowntimeDialogComponent,
  MailOrderPatientEnrollmentFormComponent,
  UpdateMailOrderProfileDialogComponent,
} from '@shared/components';
import { CMS_CONFIG_FALLBACK, COMMON_ERROR_MSG, isWithinWindow } from '@shared/utils';
import { UpdateProfileComponent } from './update-profile/update-profile.component';

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
  standalone: false,
})
export class MainComponent implements OnInit, OnDestroy {
  @ViewChild('error', { read: TemplateRef }) errorTemplate!: TemplateRef<any>;
  @ViewChild('updateProfileError', { read: TemplateRef })
  updateProfileError!: TemplateRef<any>;
  @ViewChild('success', { read: TemplateRef }) successTemplate!: TemplateRef<any>;

  private readonly subscription = new Subscription();
  public toggleSideNav = false;
  public toggleSideNavSubmenu = false;
  public toggleSideNavSubmenu1 = false;

  public toggleSideNavSubmenuHelp = false;
  public toggleSideNavSubmenuMailOrder = false;
  public isCartPage = false;
  public selectedLanguage!: string;

  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
    map((result) => result.matches),
    shareReplay()
  );

  member: Member | null = null;
  user: MemberProfile | null = null;
  url!: string;
  errorMsg!: string;
  errorMsgForUpdateProfile!: string;
  isError = false;
  clientMessage!: string;
  commonBannerMessage!: boolean;
  clientName$: Subject<boolean> = new Subject<boolean>();
  fetchCmsConfigurations$: Subject<string> = new Subject<string>();
  fetchClientConfigurations$: Subject<string> = new Subject<string>();
  aboutUsLink!: string;
  mailOrderFlyerLink!: string;
  memberDataLoaded = false;

  get getPharmacyIntegrationType(): Observable<IntegrationType> {
    return this.clientService.getPharmacyIntegrationType();
  }

  constructor(
    private readonly authService: AuthService,
    private readonly breakpointObserver: BreakpointObserver,
    public readonly multilingualService: MultilingualService,
    public readonly cartService: CartService,
    private readonly router: Router,
    public readonly userService: UserService,
    private readonly memberService: MemberService,
    public readonly cmsService: CmsService,
    public readonly clientService: ClientsService,
    private readonly snackBar: MatSnackBar,
    public readonly active: ActivatedRoute,
    private readonly appInsightsService: AppInsightsService,
    public readonly dialog: MatDialog,
    public readonly mailOrderService: MailOrderProfileEnrollmentService,
    private readonly logService: LogService
  ) {
    this.selectedLanguage = this.multilingualService.getUserLanguage();
    this.changeTextSize(AppConstants.TEXT_SIZE.SMALL);

    router.events.subscribe((o) => {
      if (o instanceof NavigationEnd) {
        this.url = o.url;
      }
    });

    this.subscription.add(
      this.clientName$
        .pipe(
          take(1),
          switchMap(() => this.getClientName())
        )
        .subscribe({
          error: () => this.appInsightsService.trackPageView(AppCenterAnalytics.LOGIN_SUCCESSFUL),
          complete: () =>
            this.appInsightsService.trackPageView(AppCenterAnalytics.LOGIN_SUCCESSFUL),
        })
    );

    this.fetchCMSConfigurations();
    this.fetchClientConfigurations();
  }

  ngOnInit(): void {
    if (this.authService.isAuthorizedByAzureB2c) {
      this.getUserName();
    } else {
      this.getmemberEligibility();
    }

    if (this.router.url.includes('cart')) {
      this.isCartPage = true;
    }

    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          if (event.url.includes('cart')) {
            this.isCartPage = true;
          } else {
            this.isCartPage = false;
          }
        }
      });
  }

  private fetchCMSConfigurations(): void {
    this.subscription.add(
      this.fetchCmsConfigurations$
        .pipe(
          take(1),
          switchMap((client) =>
            this.cmsService.loadConfigurations(client).pipe(
              take(1),
              tap((res) => {
                // Set about us link
                const aboutUs = (res.links || []).find(
                  (x) => x.name.toLowerCase() === AppConstants.LINK_NAMES.ABOUT_US.toLowerCase()
                );
                aboutUs ? (this.aboutUsLink = aboutUs.url) : null;

                // Set mail order flyer link
                const mailOrderFlyer = (res.links || []).find(
                  (x) => x.name.toLowerCase() === AppConstants.LINK_NAMES.HEALTHDYNE_MAIL_ORDER_PHARMACY_BANNER.toLowerCase()
                );
                mailOrderFlyer ? (this.mailOrderFlyerLink = mailOrderFlyer.url) : null;

                // show banner message configured at client level
                if (res.clientSpecificConfig.clientBannerMessage) {
                  this.setClientMessage(client, res.clientSpecificConfig.clientBannerMessage);
                }

                // show common banner message configured
                if (res.clientSpecificConfig.enableBannerMessage.commonHeader) {
                  this.showBannerMessage();
                }

                // check if mail order features are disabled for client
                if (res.clientSpecificConfig.suppressMailOrderFeatures) {
                  this.mailOrderService.disableMailOrderFeatures$.next(true);
                } else {
                  this.mailOrderService.disableMailOrderFeatures$.next(false);
                }
              })
            )
          )
        )
        .subscribe()
    );
  }

  private fetchClientConfigurations(): void {
    this.subscription.add(
      this.fetchClientConfigurations$
        .pipe(
          take(1),
          switchMap((client) => this.clientService.getClientConfigurations(client).pipe(take(1)))
        )
        .subscribe()
    );
  }

  private getUserName(): void {
    this.memberService
      .getUserName(this.userService.getObjectId())
      .pipe(
        take(1),
        switchMap((userName) => {
          this.appInsightsService.setUsernameForAppInsights(userName);
          return this.getMemberDetails(userName);
        })
      )
      .subscribe({
        next: () => this.getmemberEligibility(),
        error: (err) => {
          this.isError = true;
          this.errorMsg = err ? err : COMMON_ERROR_MSG;
        },
      });
  }

  private getMemberDetails(userName: string): Observable<MemberProfile> {
    return this.memberService.getMemberDetails(userName).pipe(
      tap((user) => {
        this.user = user;
        this.userService.memberProfile = user;

        // On first login, mark registration as complete
        if (!this.user.isRegistered && this.authService.isAuthorizedByAzureB2c)
          this.markRegistrationComplete(user);
      })
    );
  }

  private markRegistrationComplete(user: MemberProfile): void {
    const body = {
      cardId: user.cardId ?? '',
      empirxMemberId: user.empirxMemberId ?? '',
    };
    this.memberService.markRegistrationComplete(body).subscribe();
  }

  private getSsoMemberDetails(
    eligibilityResponse: EligibilityVerificationResponse | null,
    cardId: string,
    empirxMemberId?: string
  ): void {
    this.memberService
      .getSsoMemberDetails(cardId, empirxMemberId)
      .pipe(take(1))
      .subscribe({
        next: (user) => {
          this.user = user;
          this.userService.memberProfile = user;
          this.updateCardId(eligibilityResponse);
        },
        error: (error) => {
          this.isError = true;
          this.errorMsg = error ? error : COMMON_ERROR_MSG;
        },
      });
  }

  private updateCardId(response: EligibilityVerificationResponse | null): void {
    // UPDATE CARD ID LOGIC
    if (this.authService.isAuthorizedByAzureB2c) {
      // NON SSO CARD ID UPDATE
      if (this.userService.memberProfile.username && this.user?.cardId != response?.cardId) {
        this.memberService
          .updateMemberCardId(this.userService.memberProfile.username, response?.cardId ?? '')
          .subscribe(
            (res) => {
              if (this.user) {
                this.user.cardId = response?.cardId ?? '';
              }
              this.appInsightsService.trackPageView(AppCenterAnalytics.UPDATE_CARD_ID_ATTEMPT, {
                Response: res,
              });
              this.logService.log(`[MainComponent] CARD ID UPDATED IN BACKEND.`, res);
            },
            (err) => {
              this.appInsightsService.trackPageView(AppCenterAnalytics.UPDATE_CARD_ID_API_ERROR, {
                Response: err,
              });
            }
          );
      }
    } else {
      // SSO USER CARD ID UPDATE
      if (this.user?.cardId != response?.cardId && this.user && this.user.username) {
        this.memberService.updateMemberCardId(this.user?.username, response?.cardId ?? '').subscribe(
          (res) => {
            if (this.user) {
              this.user.cardId = response?.cardId ?? '';
            }
            this.appInsightsService.trackPageView(AppCenterAnalytics.UPDATE_CARD_ID_ATTEMPT, {
              Response: res,
            });
            this.logService.log(`[MainComponent] CARD ID UPDATED IN BACKEND.`, res);
          },
          (err) => {
            this.appInsightsService.trackPageView(AppCenterAnalytics.UPDATE_CARD_ID_API_ERROR, {
              Response: err.error,
            });
          }
        );
      }
    }
  }

  private getClientName(): Observable<string | null> {
    if (this.userService.eligibilityVerificationResponse?.clientId) {
      return this.clientService
        .fetchClientDetails(this.userService.eligibilityVerificationResponse?.clientId)
        .pipe(
          take(1),
          tap((res) => this.clientService.setClientName(res.clientShort_Name)),
          map((res) => res.clientShort_Name)
        );
    } else {
      return of(null);
    }
  }

  private getmemberEligibility() {
    let eligibilityAPI: Observable<EligibilityVerificationResponse>;

    if (this.authService.isAuthorizedBySSO) {
      const { clientId, cardId, personCode, sequenceCode } =
        this.userService.getSsoMemberDemographics();

      eligibilityAPI = this.userService
        .getSSOMemberEligibility(clientId, cardId, personCode, sequenceCode)
        .pipe(take(1));
    } else {
      eligibilityAPI = this.userService.getMemberEligbilityByUsername().pipe(take(1));
    }

    eligibilityAPI
      .pipe(
        filter((response) => !!response),
        tap((response) => {
          this.userService.eligibilityVerificationResponse = response;
          this.appInsightsService.setClientIdForAppInsights(
            this.userService.eligibilityVerificationResponse?.clientId || ''
          );
          // Update CardId Logic
          this.updateCardId(response);

          this.fetchCmsConfigurations$.next(response.clientId);
          this.fetchClientConfigurations$.next(response.clientId);
          this.clientName$.next(true);
        }),
        switchMap((response) => {
          // If the member is termed, we need to check if the disable termed after date has passed.
          // If it has passed, member is not allowed to access the Portal
          if (response.eligibilityStatus === ELIGIBILITY_STATUS.TERMED) {
            return this.getDisableTermedAfter(response.clientId, response.eligibilityEndDate).pipe(
              map((disableTermedAfter) => ({
                memberEligibility: response,
                disableTermedAfter,
              }))
            );
          }
          return of({ memberEligibility: response, disableTermedAfter: null as Date | null });
        })
      )
      .subscribe({
        next: ({ memberEligibility, disableTermedAfter }) => {
          const isValidMember =
            !!memberEligibility?.cardId &&
            !!memberEligibility?.clientId &&
            !!memberEligibility?.personCode;

          const hasValidEligibility =
            memberEligibility.eligibilityStatus === ELIGIBILITY_STATUS.ELIGIBLE ||
            memberEligibility.eligibilityStatus === ELIGIBILITY_STATUS.FUTURE_EFFECTIVE ||
            (memberEligibility.eligibilityStatus === ELIGIBILITY_STATUS.TERMED &&
              disableTermedAfter != null &&
              disableTermedAfter > new Date());

          // If the member is not valid or does not have valid eligibility, set error state and message
          if (!isValidMember || !hasValidEligibility) {
            this.isError = true;
            this.errorMsg = this.multilingualService.langMap?.LABEL_ELIGIBILITY_FAILED;
            return;
          }

          // If the member is valid and has valid eligibility, proceed with fetching family coverage
          this.getFamilyCoverage(memberEligibility.cardId, memberEligibility.clientId);

          // If profile is not verified, open the update profile dialog for eligible members
          if (
            this.userService.memberProfile &&
            !this.userService.memberProfile.isVerified &&
            memberEligibility.eligibilityStatus === ELIGIBILITY_STATUS.ELIGIBLE
          ) {
            this.openUpdateProfileDialog();
          }
        },
        error: (err) => {
          this.showOutagePopup();
          this.isError = true;
          this.errorMsg = err ? err : COMMON_ERROR_MSG;
        },
      });
  }

  private getDisableTermedAfter(clientId: string, eligibilityEndDateStr: string): Observable<Date> {
    const getDisableDate = (disableAfterMonths: number): Date => {
      const disableDate = new Date(eligibilityEndDateStr);
      disableDate.setMonth(disableDate.getMonth() + disableAfterMonths);
      return disableDate;
    };

    // Fetch the disable termed after configuration from CMS (In Months).
    // If not configured, use the fallback value.
    return this.cmsService.getCombinedCmsConfiguration(clientId).pipe(
      map((res) => {
        const configuredValue = Number(res.commonConfig?.['DISABLE_TERMED_USER_AFTER']);
        const disableAfterMonths =
          configuredValue > 0 ? configuredValue : CMS_CONFIG_FALLBACK.DISABLE_TERMED_USER_AFTER;

        return getDisableDate(disableAfterMonths);
      }),
      catchError(() => of(getDisableDate(CMS_CONFIG_FALLBACK.DISABLE_TERMED_USER_AFTER)))
    );
  }

  private showOutagePopup(): void {
    const defaultSettings = {
      show: false,
      message: '',
      title: '',
      is_complete_shutdown: false,
    };

    this.cmsService
      .getMaintenanceMode()
      .pipe(
        map((res) => {
          try {
            const configuration = Array.isArray(res) && res.length > 0 ? res[0] : null;
            if (!configuration || !configuration.display_flag) {
              return defaultSettings;
            }

            const now = Date.now();
            const isMaintenanceActive = isWithinWindow(
              configuration.start_date,
              configuration.end_date,
              now
            );

            const shutdownCfg = configuration.complete_shutdown_settings;
            const hasShutdownConfigured = !!configuration.is_complete_shutdown && !!shutdownCfg;

            const isShutdownActive =
              hasShutdownConfigured &&
              isWithinWindow(shutdownCfg.start_date, shutdownCfg.end_date, now);

            // Complete shutdown takes precedence over maintenance
            if (isShutdownActive) {
              const texts = this.getMaintenanceTexts(shutdownCfg);
              return this.buildOutageSettings(texts.title, texts.message, true);
            }

            if (isMaintenanceActive) {
              const texts = this.getMaintenanceTexts(configuration);
              return this.buildOutageSettings(texts.title, texts.message, false);
            }

            return defaultSettings;
          } catch {
            return defaultSettings;
          }
        }),
        switchMap((settings) => {
          this.logService.log(`[MainComponent] Maintenance mode settings`, settings);

          if (settings.show) {
            return this.callOutagePopup(
              settings.title,
              settings.message,
              settings.is_complete_shutdown
            ).pipe(map((res) => res));
          }

          return of(settings);
        }),
        catchError(() => of(defaultSettings))
      )
      .subscribe();
  }

  private openUpdateProfileDialog(): void {
    const dialogRef = this.dialog.open(UpdateProfileComponent, {
      disableClose: true,
      data: false,
      width: '25vw',
    });

    this.subscription.add(
      dialogRef.afterClosed().subscribe((result) => {
        switch (result?.dialogStatus) {
          case 0:
            this.snackBar.openFromTemplate(this.successTemplate, {
              duration: 4000,
              verticalPosition: 'top',
              horizontalPosition: 'center',
            });
            break;
          case 1:
            this.errorMsgForUpdateProfile = result.msg;
            this.snackBar.openFromTemplate(this.updateProfileError, {
              duration: 4000,
              verticalPosition: 'top',
              horizontalPosition: 'center',
            });
            break;
        }
      })
    );
  }

  // Helper to construct outage settings object.
  private buildOutageSettings(title: string, message: string, isCompleteShutdown: boolean) {
    return {
      show: true,
      message,
      title,
      is_complete_shutdown: isCompleteShutdown,
    };
  }

  private getMaintenanceTexts(configuration: {
    message_en: string;
    message_es: string;
    title_en: string;
    title_es: string;
  }): { message: string; title: string } {
    if (this.multilingualService.selectedLang === AppConstants.LANGUAGE.SPANISH) {
      return {
        message: configuration.message_es,
        title: configuration.title_es,
      };
    } else {
      return {
        message: configuration.message_en,
        title: configuration.title_en,
      };
    }
  }

  private callOutagePopup(title: string, message: string, outageFlag: boolean): Observable<any> {
    this.appInsightsService.trackPageView(AppCenterAnalytics.PLANNED_OUTAGE_MSG_DISPLAYED, {
      data: {
        title: title,
        message: message,
        isCompleteShutdown: outageFlag,
      },
    });
    const dialogRef = this.dialog.open(DowntimeDialogComponent, {
      disableClose: true,
      data: { title, message, outageFlag },
    });
    return dialogRef.afterClosed().pipe(
      take(1),
      tap(() => {
        this.appInsightsService.trackPageView(AppCenterAnalytics.PLANNED_OUTAGE_MSG_CLOSED);
      })
    );
  }

  private getFamilyCoverage(cardId: string, clientId: string): void {
    this.memberService
      .familyCoverage(cardId, clientId)
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this.memberDataLoaded = true;
          this.userService.familyCoverageResponse = res;
          this.userService.allFamilyCoverageResponse = { ...res };

          if (this.userService.eligibilityVerificationResponse) {
            this.userService.member =
              this.memberService.getCurrentMember(
                this.userService.familyCoverageResponse,
                this.userService.eligibilityVerificationResponse.personCode
              ) || null;
            this.member = this.userService.member;
          }

          this.userService.familyCoverageResponse.members =
            this.userService.familyCoverageResponse.members.filter((o) =>
              this.userService.member?.allowedPhiPersonCodes.includes(o.personCode)
            );

          this.checkToFetchDigitalIdCard(clientId);

          if (this.authService.isAuthorizedBySSO) {
            this.appInsightsService.trackPageView(AppCenterAnalytics.SSO_USER_LOGGED_IN);

            const { cardId, empirxMemberId } = this.userService.getSsoMemberDemographics();
            this.getSsoMemberDetails(
              this.userService.eligibilityVerificationResponse,
              cardId,
              empirxMemberId
            );
          }

          // check if mail order profile is created for member
          this.showUpdateMailOrderProfileDialog();
        },
        error: (error) => {
          // if the API fails for any reason, the check if there is any scheduled downtime
          this.showOutagePopup();
          this.isError = true;
          this.errorMsg = error ? error : COMMON_ERROR_MSG;
        },
      });
  }

  private checkToFetchDigitalIdCard(clientId: string): void {
    // Check if Digital ID Cards are disabled for the client ID
    this.subscription.add(
      this.cmsService
        .loadConfigurations(clientId)
        .pipe(take(1))
        .subscribe((result) => {
          const disableDigitalIds = result.clientSpecificConfig.disableDigitalIDCards;

          if (!disableDigitalIds) {
            this.getIdCardImage(this.userService.member);
          }
        })
    );
  }

  private showUpdateMailOrderProfileDialog() {
    this.mailOrderService
      .showUpdateProfileDialog()
      .pipe(
        take(1),
        switchMap((res) => {
          if (res) {
            // open dialog
            const dialogRef = this.openUpdateMailOrderProfileDialog();
            this.showOutagePopup();
            return this.processUpdateProfileDecision(dialogRef);
          } else {
            this.showOutagePopup();
            return of(false);
          }
        })
      )
      .subscribe();
  }

  private processUpdateProfileDecision(
    dialogRef: MatDialogRef<UpdateMailOrderProfileDialogComponent, any>
  ): Observable<unknown> {
    return dialogRef.afterClosed().pipe(
      switchMap(
        (res: {
          action: 'update-now' | 'update-later' | null;
          selectedMember: MailOrderFamilyMember;
        }) => {
          return defer(() => {
            if (res.action === 'update-now') {
              const dialogRef2 = this.dialog.open(MailOrderPatientEnrollmentFormComponent, {
                height: 'auto',
                width: '60vw',
                panelClass: 'update-profile-dialog',
                data: {
                  member: res.selectedMember.memberProfile,
                  pageName: PAGE_NAME.HOME_UPDATE_DIALOG,
                },
                disableClose: true,
              });
              return dialogRef2.afterClosed().pipe(
                tap(() => {
                  this.mailOrderService.hideUpdateProfileMessage$.next(true);
                })
              );
            } else {
              return of(false);
            }
          });
        }
      )
    );
  }

  private openUpdateMailOrderProfileDialog(): MatDialogRef<UpdateMailOrderProfileDialogComponent> {
    return this.dialog.open(UpdateMailOrderProfileDialogComponent, {
      height: 'auto',
      width: '500px',
      hasBackdrop: true,
      disableClose: true,
      data: {
        loadMembersByPHI: true,
      },
    });
  }

  private getIdCardImage(member: Member | null): void {
    if (!member) {
      this.member = this.userService.member;
      return;
    }
    this.memberService
      .temporaryIdCard(member.personCode)
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this.createImageFromBlob(member, res);
        },
        error: (error) => {
          this.member = this.userService.member;
          if (this.member) {
            this.member.cardImageError = error ? error : COMMON_ERROR_MSG;
          }
        },
      });
  }

  private createImageFromBlob(member: Member, image: Blob): void {
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      () => {
        member.cardImage = reader.result as string;
        this.member = this.userService.member;
      },
      false
    );
    if (image) {
      if (image.type !== 'application/pdf') {
        reader.readAsDataURL(image);
      }
    }
  }

  public onLogoutClick(): void {
    this.dialog
      .open(CommonConfirmationPopupComponent, {
        data: {
          msg: this.multilingualService?.langMap?.MESSAGE_CONFIRM_LOGOUT,
          title: this.multilingualService?.langMap?.LABEL_MORE_LOGOUT,
          confirmButtonText: this.multilingualService.langMap?.LABEL_HOME_YES,
          cancelButtonText: this.multilingualService.langMap?.LABEL_HOME_NO,
          showCancelButton: true,
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result == 'confirm') {
          this.logout();
        }
      });
  }

  logout(): void {
    this.authService.logout();
  }

  public toggleSideNavfn(): void {
    this.toggleSideNav = !this.toggleSideNav;
  }

  public toggleSideNavSubmenufn(): void {
    this.toggleSideNavSubmenu = !this.toggleSideNavSubmenu;
    this.toggleSideNavSubmenuHelp = false;
    this.toggleSideNavSubmenuMailOrder = false;
    this.toggleSideNavSubmenu1 = false;
  }

  public toggleSideNavSubmenuHelpfn(): void {
    this.toggleSideNavSubmenuHelp = !this.toggleSideNavSubmenuHelp;
    this.toggleSideNavSubmenu = false;
    this.toggleSideNavSubmenuMailOrder = false;
    this.toggleSideNavSubmenu1 = false;
  }

  public toggleSideNavSubmenuReviewfn(): void {
    this.toggleSideNavSubmenu1 = !this.toggleSideNavSubmenu1;
    this.toggleSideNavSubmenu = false;
    this.toggleSideNavSubmenuHelp = false;
    this.toggleSideNavSubmenuMailOrder = false;
  }

  public toggleSideNavSubmenuMailfn(): void {
    this.toggleSideNavSubmenuMailOrder = !this.toggleSideNavSubmenuMailOrder;
    this.toggleSideNavSubmenu = false;
    this.toggleSideNavSubmenu1 = false;
    this.toggleSideNavSubmenuHelp = false;
  }

  public changeLang(lang: string): void {
    this.multilingualService.getLangMap(lang);
    this.multilingualService.langMapFailureObservable.subscribe(() => {
      this.selectedLanguage = this.multilingualService.getUserLanguage();
      this.snackBar.openFromTemplate(this.errorTemplate, {
        duration: 4000,
        verticalPosition: 'top',
        horizontalPosition: 'center',
      });
    });
  }

  public closeAll(): void {
    this.toggleSideNavSubmenu = false;
    this.toggleSideNavSubmenuHelp = false;
  }

  aboutUsClicked(): void {
    this.appInsightsService.trackPageView(AppCenterAnalytics.ABOUT_US_VIEWED);
  }

  mailOrderFlyerClicked(): void {
    this.appInsightsService.trackPageView(AppCenterAnalytics.HELP_PHARMACY_FLYER_PDF_VIEWED);
  }

  settingsOpenUpdateProfile(): void {
    const dialogRef = this.dialog.open(UpdateProfileComponent, { data: true, width: '25vw' });
    this.subscription.add(
      dialogRef.afterClosed().subscribe((result) => {
        if (result.dialogStatus === 0) {
          if (this.authService.isAuthorizedByAzureB2c) {
            this.getUserName();
          } else {
            this.getSsoMemberDetails(
              this.userService.eligibilityVerificationResponse,
              this.userService.eligibilityVerificationResponse?.cardId ?? '',
              this.userService.eligibilityVerificationResponse?.empirxMemberId ?? ''
            );
          }
          this.snackBar.openFromTemplate(this.successTemplate, {
            duration: 4000,
            verticalPosition: 'top',
            horizontalPosition: 'center',
          });
        }

        if (result.dialogStatus === 1) {
          this.errorMsgForUpdateProfile = result.msg;
          this.snackBar.openFromTemplate(this.updateProfileError, {
            duration: 4000,
            verticalPosition: 'top',
            horizontalPosition: 'center',
          });
        }
      })
    );
  }

  public changeTextSize(textSizeValue: string): void {
    let fontSize: string;
    this.multilingualService.selectedTextSize = textSizeValue;

    switch (textSizeValue) {
      case AppConstants.TEXT_SIZE.MEDIUM: {
        fontSize = '18px';
        break;
      }
      case AppConstants.TEXT_SIZE.LARGE: {
        fontSize = '20px';
        break;
      }
      default: {
        fontSize = '16px';
        break;
      }
    }
    document.getElementsByTagName('html')[0].style.fontSize = fontSize;
    const bodyClasses = document.getElementsByTagName('body')[0].classList;
    bodyClasses.remove(`${AppConstants.TEXT_SIZE.LARGE}-text-size`);
    bodyClasses.remove(`${AppConstants.TEXT_SIZE.MEDIUM}-text-size`);
    bodyClasses.remove(`${AppConstants.TEXT_SIZE.SMALL}-text-size`);

    bodyClasses.add(`${textSizeValue}-text-size`);
  }

  private setClientMessage(clientId: string, bannerMessage: string): void {
    this.clientMessage = bannerMessage;
    const data = {
      clientId: clientId,
      message: bannerMessage,
    };
    this.appInsightsService.trackPageView(
      `${AppCenterAnalytics.HOME_SCREEN_CLIENT_MESSAGE_DISPLAYED}`,
      { data }
    );
  }

  private showBannerMessage(): void {
    const message = this.multilingualService?.langMap?.LABEL_COMMON_HEADER_BANNER_MESSAGE;
    const data = {
      sourcePage: PAGE_NAME.HOME_BANNER,
      message,
    };
    this.commonBannerMessage = true;
    this.appInsightsService.trackPageView(`${AppCenterAnalytics.BANNER_MESSAGE_DISPLAYED}`, {
      data,
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
